import React, { useEffect, useRef, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FaLinkedin,
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaTiktok,
} from "react-icons/fa";
import logo from "/images/logo.svg";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const socialIconMapping = {
  LinkedIn: FaLinkedin,
  Facebook: FaFacebook,
  Instagram: FaInstagram,
  Twitter: FaTwitter,
  YouTube: FaYoutube,
  TikTok: FaTiktok,
};

const Footer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const contentRef = useRef(null);

  const isActiveLink = useCallback(
    (path) => location.pathname === path,
    [location.pathname]
  );
  const getBrandData = () => {
    try {
      return t("components.shared.footer", { returnObjects: true }).brand || {};
    } catch {
      return {
        name: "HER Energy",
        logoAlt: "Logo",
        tagline: "Renewable Energy Solutions",
      };
    }
  };
  const getSectionsData = () => {
    try {
      return (
        t("components.shared.footer", { returnObjects: true }).sections || {}
      );
    } catch {
      return {};
    }
  };
  useEffect(() => {
    const currentContentRef = contentRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("footer-content-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (currentContentRef) {
      observer.observe(currentContentRef);
    }

    return () => {
      if (currentContentRef) {
        observer.unobserve(currentContentRef);
      }
    };
  }, []);

  const brand = getBrandData();
  const sections = getSectionsData();

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <footer
      className="bg-black text-gray-300 py-16"
      style={{
        width: "100%",
        paddingBottom: "3rem",
      }}
      id="safePad"
    >
      <div className="w-full flex justify-center mb-10">
        <div className="w-[80%] h-[2px] bg-gradient-to-r from-[#e1393e] via-[#fd6e4c] to-[#ffd450]"></div>
      </div>

      <motion.div
        className="container mx-auto px-6"
        style={{ maxWidth: "1200px" }}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <motion.div className="col-span-1" variants={itemVariants}>
            <div className="flex items-center gap-4 mb-4">
              <img
                src={logo}
                alt={brand.logoAlt || "Logo"}
                width="48"
                height="48"
                style={{ objectFit: "contain" }}
              />
              <h2 className="text-xl font-bold bg-gradient-to-r from-[#e1393e] via-[#fd6e4c] to-[#ffd450] bg-clip-text text-transparent">
                {brand.name || "HER Energy"}
              </h2>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              {brand.tagline || "Renewable Energy Solutions"}
            </p>
          </motion.div>

          {Object.entries(sections).map(([key, section]) => (
            <motion.div
              key={key}
              className="col-span-1"
              variants={itemVariants}
            >
              <h3 className="text-white text-sm font-medium mb-4">
                {section.title || ""}
              </h3>
              {key !== "social" ? (
                <ul className="space-y-3">
                  {(section.items || []).map((item, index) => (
                    <li key={index} className="text-gray-400 text-sm">
                      {isActiveLink(item.href) ? (
                        <span className="text-white font-medium cursor-default">
                          {item.name || ""}
                        </span>
                      ) : (
                        <NavLink
                          to={item.href || "#"}
                          className="hover:text-white transition-colors duration-300"
                          onClick={() => window.scrollTo(0, 0)}
                        >
                          {item.name || ""}
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex space-x-4 mt-4">
                  {(section.items || []).map((item, index) => {
                    const IconComponent = socialIconMapping[item.platform];
                    return (
                      <a
                        key={index}
                        href={item.href || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: item.color || "#fff" }}
                        className="transition-colors duration-300 hover:text-[#fd6e4c]"
                        aria-label={`Visit our ${item.platform} page`}
                      >
                        {IconComponent && (
                          <IconComponent
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        )}
                      </a>
                    );
                  })}
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-12 pt-8 border-t border-gray-600/50 flex flex-col sm:flex-row justify-between items-center pb-6"
          variants={itemVariants}
        >
          <p className="text-xs text-gray-400">
            © {currentYear} {brand.name || "HER Energy"}. All rights reserved.
          </p>
        </motion.div>
      </motion.div>
    </footer>
  );
};

export default React.memo(Footer);
